import {
  Calendar,
  Clock,
  Pencil,
  Trash2,
  NotebookPen,
  UserRound,
  School,
} from "lucide-react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useGetTeacherById } from "../../query/teacherQuery";
import { useGetChildById } from "../../query/childQuery";
import { useGetClassById } from "../../query/classQuery";
import {
  assigneeLabel,
  childName,
  classroomName,
  homeworkTypeLabel,
  teacherName,
  typeBadgeClass,
} from "./homeworkHelpers";
import { DetailCoverShowcase } from "../../components/DetailCoverHero";
import "../teachers/teacherDetails.css";
import "../activities/activities.css";
import "./homework.css";

export default function HomeworkProfile({ homework, onDelete }) {
  const navigate = useNavigate();

  const teacherId = homework?.teacher?._id || homework?.teacher;
  const childId = homework?.children?._id || homework?.children;
  const classroomId = homework?.classroom?._id || homework?.classroom;

  const { data: teacher } = useGetTeacherById(teacherId);
  const { data: child } = useGetChildById(childId);
  const { data: classroom } = useGetClassById(classroomId);

  const teacherDoc = homework?.teacherDoc || teacher;
  const childDoc = homework?.childDoc || child;
  const classroomDoc = homework?.classroomDoc || classroom;

  return (
    <div className="td-page">
      <div className="td-main">
        <article className="td-card">
          <DetailCoverShowcase
            coverKey={homework?._id}
            backLabel="Back to homework"
            onBack={() => navigate("/homework")}
          >
            <div className="act-detail-cover-panel">
              <NotebookPen size={48} strokeWidth={1.5} color="#2563eb" />
              <p style={{ margin: "12px 0 0", fontSize: 14, color: "#64748b" }}>Homework entry</p>
            </div>
          </DetailCoverShowcase>

          <div className="act-detail-body">
            <div className="act-detail-head">
              <div className="act-detail-head-copy">
                <span className="act-detail-kicker">
                  <NotebookPen size={14} />
                  {homeworkTypeLabel(homework.type)}
                </span>
                <div className="act-detail-title-row">
                  <h2>{homework.title || "Homework"}</h2>
                  <span className={`data-pill hw-type-badge ${typeBadgeClass(homework.type)}`}>
                    {homeworkTypeLabel(homework.type)}
                  </span>
                </div>
              </div>
            </div>

            <div className="act-detail-meta">
              <span>
                <i className="is-orange">
                  <Calendar size={14} />
                </i>
                Due {homework.dueDate ? dayjs(homework.dueDate).format("MMMM D, YYYY") : "—"}
              </span>
              <span>
                <i className="is-orange">
                  <Clock size={14} />
                </i>
                Assigned {homework.assignDate ? dayjs(homework.assignDate).format("MMMM D, YYYY") : "—"}
              </span>
            </div>

            <section className="act-detail-section">
              <h3>Description</h3>
              {homework.description ? (
                <p className="hw-detail-desc">{homework.description}</p>
              ) : (
                <p className="hw-detail-desc">No description provided.</p>
              )}
            </section>

            <section className="act-detail-section">
              <h3>Assignment details</h3>
              <div className="fee-detail-meta-grid">
                <div className="fee-detail-meta-item">
                  <span>Assignee type</span>
                  <strong>{assigneeLabel(homework.assignee)}</strong>
                </div>
                <div className="fee-detail-meta-item">
                  <span>
                    <UserRound size={12} style={{ display: "inline", marginRight: 4 }} />
                    Teacher
                  </span>
                  <strong>{teacherName(teacherDoc)}</strong>
                </div>
                {homework.assignee === "CLASS" ? (
                  <div className="fee-detail-meta-item">
                    <span>
                      <School size={12} style={{ display: "inline", marginRight: 4 }} />
                      Classroom
                    </span>
                    <strong>{classroomName(classroomDoc)}</strong>
                  </div>
                ) : (
                  <div className="fee-detail-meta-item">
                    <span>
                      <UserRound size={12} style={{ display: "inline", marginRight: 4 }} />
                      Student
                    </span>
                    <strong>{childName(childDoc)}</strong>
                  </div>
                )}
              </div>
            </section>
          </div>
        </article>

        <div className="td-actions act-detail-actions">
          <button
            type="button"
            className="td-action is-primary"
            onClick={() => navigate(`/homework/edit/${homework._id}`)}
          >
            <Pencil size={15} />
            Edit homework
          </button>
          <button type="button" className="td-action is-danger" onClick={onDelete}>
            <Trash2 size={15} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}
